import {getTestProps} from '@benchling/js-test-utils';
import {Box, Loader, Center, Button, Group, Flex, createStyles} from '@mantine/core';
import {captureException} from '@sentry/browser';
import DOMPurify from 'dompurify';
import React, {useEffect, useRef} from 'react';

import {generateReactionImage} from 'chemistry/ChemistryDAO';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {ActionMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  StructureWithFormat,
  getStructureWithFormatFromFile,
} from 'notebook/integrations/chemistry/reaction-scheme/ReactionSketcherModal';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {isFetchConnectionError} from 'util/FetchErrors';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

interface MarvinReactionData {
  sanitizedReactionSVG: string;
  marvinStructureContent: string;
}

// Upper bound on a single load: exports wait on this call's loading indicator, and no underlying request
// has its own timeout, so a stall here would otherwise pin the export indicator indefinitely.
export const REACTION_SCHEME_LOAD_TIMEOUT_MS = 30_000;

// Margin around the trimmed diagram, in SVG user units. Marvin's scheme SVGs (`scale30`) draw bonds about 1.9
// units wide; without a margin, the browser's pixel-snapped clip of the <svg> can shave the outermost bonds and
// leave rings at the diagram's edge visibly open.
const REACTION_SCHEME_SVG_PADDING = 2;

async function fetchMarvinContentAndPreview(
  externalFileId: string,
  externalFileUrl: string,
  entryId: string
): Promise<MarvinReactionData | null> {
  // Abort the in-flight fetch and settle the load if it overruns the timeout budget.
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort();
  }, REACTION_SCHEME_LOAD_TIMEOUT_MS);
  // Resolve every failure mode to null (rendered as the error banner). useAsyncState only maps server
  // request errors to its ERROR state; any other rejection would otherwise stay LOADING and pin the
  // export indicator.
  try {
    // Race against the timeout so a stall anywhere in the sequence settles the call rather than hanging.
    return await Promise.race([
      loadMarvinContentAndPreview(externalFileId, externalFileUrl, entryId, abortController.signal),
      rejectWhenAborted(abortController.signal),
    ]);
    // eslint-disable-next-line benchlint/always-throw-from-catch -- intentional terminal null; see below
  } catch (err) {
    // Report unexpected failures (decode/render, timeout) for diagnosability. Skip server request errors,
    // which are already surfaced to the user, and browser-level fetch connection failures (offline, flaky
    // network, extensions), which are not actionable and are filtered app-wide in ErrorConfig. Either way
    // resolve to the terminal null so the user gets the error banner with its Refresh action.
    if (!net.isServerRequestError(err) && !isFetchConnectionError(err)) {
      captureException(err);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function loadMarvinContentAndPreview(
  externalFileId: string,
  externalFileUrl: string,
  entryId: string,
  signal: AbortSignal
): Promise<MarvinReactionData | null> {
  const signedUrl = await performSignedFileQuery(externalFileId, externalFileUrl, entryId);
  if (signedUrl == null) {
    return null;
  }
  const response = await fetch(signedUrl, {signal});
  if (!response.ok) {
    return null;
  }
  // Note: could port this to use chemicalStructureFromFile
  const marvinStructureContent = await response.text();
  const {base64SVG} = await generateReactionImage(marvinStructureContent);
  // Decode and sanitize inside the try/catch so a malformed image settles to the error banner instead
  // of throwing through render; doing it once here also avoids re-sanitizing on every render.
  const sanitizedReactionSVG = DOMPurify.sanitize(atob(base64SVG));
  return {sanitizedReactionSVG, marvinStructureContent};
}

function rejectWhenAborted(signal: AbortSignal): Promise<never> {
  return new Promise((_resolve, reject) => {
    signal.addEventListener(
      'abort',
      () => {
        reject(new DOMException('Reaction scheme load timed out', 'TimeoutError'));
      },
      {once: true}
    );
  });
}

export default function ReactionSchemeContent({
  externalFileId,
  externalFileUrl,
  entryId,
  locked = false,
  openSketcherModalWithStructureAndFormat,
  isSyncing,
}: {
  externalFileId: string;
  externalFileUrl: string;
  entryId: string;
  locked?: boolean;
  openSketcherModalWithStructureAndFormat: (
    structureWithFormat: StructureWithFormat,
    actionMethod: ActionMethod
  ) => void;
  isSyncing: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(classes.reactionSVG, REACTION_SCHEME_SVG_PADDING);
  const {currentState: marvinContentAndPreviewAsyncState, makeCall: fetchMarvinContentAndPreviewAsync} =
    useAsyncState({makeCall: fetchMarvinContentAndPreview, keepOnlyLatest: true});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
  }, [externalFileId, externalFileUrl, entryId, fetchMarvinContentAndPreviewAsync]);

  const marvinContent =
    marvinContentAndPreviewAsyncState.status === AsyncStatus.SUCCESS &&
    marvinContentAndPreviewAsyncState.data != null
      ? marvinContentAndPreviewAsyncState.data
      : null;

  async function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = e.target.files;
    if (!files || files.length !== 1) {
      return;
    }
    try {
      const structureWithFormat = await withLoadingScreen(getStructureWithFormatFromFile(files[0]));
      if (structureWithFormat != null) {
        openSketcherModalWithStructureAndFormat(structureWithFormat, ActionMethod.UPLOAD);
      }
    } finally {
      // Reset the input value to empty string to allow the same file to be selected again.
      e.target.value = '';
    }
  }

  const isPreviewLoading = marvinContentAndPreviewAsyncState.status === AsyncStatus.LOADING;
  if (isSyncing || isPreviewLoading) {
    // Mark only the bounded preview load as an export indicator so exports wait for the diagram, not the
    // spinner. isSyncing is excluded: it reflects an un-timed table-to-diagram rebuild that could pin the
    // indicator, and its completion produces a new file whose preview load re-raises the indicator anyway.
    return (
      <Center
        h="100%"
        {...(isPreviewLoading ? loadingIndicatorAttrs() : {})}
        {...getTestProps({component: 'ReactionSchemeContent', element: 'loadingIndicator'})}
      >
        <Loader size="xl" />
      </Center>
    );
  } else if (marvinContent != null) {
    return (
      <Flex direction="column" w="100%" h="100%" gap="xs">
        {!locked && (
          <Group position="right">
            {/* Note that the Mantine dropzone uses the File System API through react-dropzone when possible instead of an input element */}
            <input
              {...getTestProps({component: 'ReactionSchemeContent', element: 'fileInput'})}
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{display: 'none'}}
              multiple={false}
            />
            <Button
              // eslint-disable-next-line no-restricted-syntax
              variant="subtle"
              leftIcon={<BenchlingIcon icon="molecule" />}
              size="sm"
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              Replace
            </Button>
            <Button
              // eslint-disable-next-line no-restricted-syntax
              variant="outline"
              leftIcon={<BenchlingIcon icon="edit" />}
              size="sm"
              onClick={() => {
                openSketcherModalWithStructureAndFormat(
                  {
                    structure: marvinContent.marvinStructureContent,
                    format: MarvinChemicalFormat.MRV,
                  },
                  ActionMethod.BUTTON_CLICK
                );
              }}
            >
              Edit
            </Button>
          </Group>
        )}
        <Box
          ref={reactionSVGRef}
          role="img"
          aria-label="Reaction diagram"
          w="100%"
          h="100%"
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: marvinContent.sanitizedReactionSVG,
          }}
        />
      </Flex>
    );
  } else {
    // TODO: Use design-approved error state UI
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Failed to load reaction scheme"
        action={{
          actionType: InlineBannerActionType.CUSTOM,
          actionText: 'Refresh',
          rightAlign: true,
          actionOnClick: () => {
            fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
          },
        }}
      />
    );
  }
}

// Constrain the SVG to its container instead of its intrinsic viewBox size. Expressed with createStyles
// (not Aphrodite) so the Emotion output is captured by the export pipeline and survives in exports.
const useStyles = createStyles(() => ({
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
}));
